import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, Eye, Pencil, Check, Loader2 } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { apiSaveContract } from "../../api";
import { uid } from "../../lib/contractTemplate";
import { exportContractPdf } from "../../lib/contractPdf";
import SendWhatsApp from "./SendWhatsApp";
import ContractPages from "./ContractPages";
import { ClausesEditor } from "./ClausesEditor";
import PlatformPanel from "./PlatformPanel";

const Section = ({ title, children }) => (
  <section className="space-y-2.5 border-b border-slate-200 pb-5">
    <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{title}</h3>
    {children}
  </section>
);

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs text-slate-500">{label}</Label>
    {children}
  </div>
);

// Vista previa escalada al ancho disponible.
const Preview = ({ contract }) => {
  const ref = useRef(null);
  const [scale, setScale] = useState(0.6);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setScale(Math.min(1, (e.contentRect.width - 32) / 794)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} className="flex justify-center p-4" data-testid="contract-preview">
      <ContractPages contract={contract} scale={scale} />
    </div>
  );
};

const ContractEditor = ({ initial, clients, onClose }) => {
  const [c, setC] = useState(initial);
  const [status, setStatus] = useState("saved");
  const [view, setView] = useState("edit");
  const [exporting, setExporting] = useState(false);
  const timer = useRef(null);
  const latest = useRef(initial);
  const pageRefs = useRef([]);

  const save = useCallback(async () => {
    clearTimeout(timer.current);
    timer.current = null;
    setStatus("saving");
    await apiSaveContract(latest.current);
    setStatus("saved");
  }, []);

  const update = (patch) => {
    setC((prev) => {
      const next = { ...prev, ...patch };
      latest.current = next;
      return next;
    });
    setStatus("pending");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => save().catch(() => setStatus("error")), 700);
  };
  const flush = () => (timer.current ? save() : Promise.resolve());
  const setPlatform = (patch) => update({ platform: { ...(latest.current.platform || {}), ...patch } });
  const synced = (doc) => {
    const next = { ...latest.current, renewals: doc.renewals, platform: doc.platform };
    latest.current = next;
    setC(next);
  };

  useEffect(() => {
    if (!exporting) return;
    const t = setTimeout(async () => {
      try {
        await exportContractPdf(pageRefs.current, latest.current.name);
      } finally {
        setExporting(false);
      }
    }, 500);
    return () => clearTimeout(t);
  }, [exporting]);

  const close = async () => {
    try {
      await flush();
    } catch (e) {}
    onClose(latest.current);
  };

  const setSigner = (i, patch) => update({ signers: c.signers.map((s, j) => (j === i ? { ...s, ...patch } : s)) });

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-slate-100" data-testid="contract-editor">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 py-2.5 sm:px-5">
        <button onClick={close} className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100" data-testid="contract-back">
          <ArrowLeft className="h-4 w-4" /> Contratos
        </button>
        <p className="hidden flex-1 truncate text-sm font-semibold text-slate-800 sm:block">{c.name}</p>
        <span className="ml-auto flex items-center gap-1 text-xs text-slate-400 sm:ml-0" data-testid="contract-status">
          {status === "saved" ? <><Check className="h-3.5 w-3.5 text-emerald-500" /> Guardado</> : status === "error" ? "Error al guardar" : <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Guardando…</>}
        </span>
        <button onClick={() => setView(view === "edit" ? "preview" : "edit")} className="rounded-lg border border-slate-200 p-2 text-slate-600 lg:hidden" data-testid="contract-toggle-view">
          {view === "edit" ? <Eye className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
        </button>
        <SendWhatsApp contract={c} client={clients.find((x) => x.id === c.client_id)} />
        <Button onClick={() => setExporting(true)} disabled={exporting} className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid="contract-pdf">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} PDF
        </Button>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className={`w-full shrink-0 space-y-5 overflow-y-auto bg-white p-4 lg:block lg:w-[460px] lg:border-r lg:border-slate-200 ${view === "edit" ? "block" : "hidden"}`}>
          <Section title="Expediente">
            <F label="Nombre del contrato (interno)">
              <Input value={c.name} onChange={(e) => update({ name: e.target.value })} data-testid="contract-name" />
            </F>
            <F label="Cliente">
              <select value={c.client_id || ""} onChange={(e) => update({ client_id: e.target.value })}
                className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="contract-client">
                <option value="">— Sin cliente —</option>
                {clients.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
              </select>
            </F>
          </Section>

          <Section title="Plataforma y renovaciones">
            <PlatformPanel contract={c} setPlatform={setPlatform} onSynced={synced} flush={flush} />
          </Section>

          <Section title="Encabezado y pie de página">
            <div className="grid grid-cols-2 gap-2">
              <F label="Encabezado izquierda"><Input value={c.headerLeft} onChange={(e) => update({ headerLeft: e.target.value })} /></F>
              <F label="Encabezado derecha"><Input value={c.headerRight} onChange={(e) => update({ headerRight: e.target.value })} /></F>
            </div>
            <F label="Pie de página"><Input value={c.footerLeft} onChange={(e) => update({ footerLeft: e.target.value })} /></F>
          </Section>

          <Section title="Portada">
            <F label="Marca"><Input value={c.brand} onChange={(e) => update({ brand: e.target.value })} /></F>
            <F label="Título"><Textarea rows={2} value={c.title} onChange={(e) => update({ title: e.target.value })} data-testid="contract-title" /></F>
            <F label="Subtítulo"><Input value={c.subtitle} onChange={(e) => update({ subtitle: e.target.value })} /></F>
            <F label="Proemio (partes que celebran)">
              <Textarea rows={6} value={c.intro} onChange={(e) => update({ intro: e.target.value })} className="text-sm" data-testid="contract-intro" />
            </F>
            <p className="text-[11px] text-slate-400">Tip: escribe **así** para poner un texto en <b>negritas</b>.</p>
          </Section>

          <Section title="Cláusulas">
            <ClausesEditor clauses={c.clauses || []} onChange={(clauses) => update({ clauses })} />
          </Section>

          <Section title="Firmas">
            {(c.signers || []).map((s, i) => (
              <div key={s.id} className="flex items-end gap-2">
                <F label={`Firmante ${i + 1}`}><Input value={s.name} onChange={(e) => setSigner(i, { name: e.target.value })} /></F>
                <F label="Cargo"><Input value={s.role} onChange={(e) => setSigner(i, { role: e.target.value })} /></F>
                <button onClick={() => update({ signers: c.signers.filter((_, j) => j !== i) })} className="mb-2 text-xs font-semibold text-red-500">Quitar</button>
              </div>
            ))}
            <button onClick={() => update({ signers: [...(c.signers || []), { id: uid(), name: "NOMBRE COMPLETO", role: "Cargo" }] })}
              className="text-xs font-semibold text-blue-600" data-testid="add-signer">
              + Agregar firmante
            </button>
          </Section>

          <Section title="Nota final">
            <Textarea rows={3} value={c.note} onChange={(e) => update({ note: e.target.value })} className="text-sm" />
          </Section>
        </aside>

        <main className={`min-w-0 flex-1 overflow-y-auto lg:block ${view === "preview" ? "block" : "hidden"}`}>
          <Preview contract={c} />
        </main>
      </div>

      {exporting && (
        <div style={{ position: "fixed", left: -10000, top: 0 }} aria-hidden="true">
          <ContractPages contract={c} pageRefs={pageRefs} />
        </div>
      )}
    </div>
  );
};

export default ContractEditor;
